'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface SidebarLayoutContextValue {
  hasSidebar: boolean;
  isExpanded: boolean;
  setHasSidebar: (value: boolean) => void;
  setIsExpanded: (value: boolean) => void;
}

const SidebarLayoutContext = createContext<SidebarLayoutContextValue | null>(null);

export function SidebarLayoutProvider({ children }: { children: ReactNode }) {
  const [hasSidebar, setHasSidebarState] = useState(false);
  const [isExpanded, setIsExpandedState] = useState(false);

  const setHasSidebar = useCallback((value: boolean) => {
    setHasSidebarState(value);
    if (!value) {
      setIsExpandedState(false);
    }
  }, []);

  const setIsExpanded = useCallback((value: boolean) => {
    setIsExpandedState(value);
  }, []);

  const value = useMemo(
    () => ({
      hasSidebar,
      isExpanded,
      setHasSidebar,
      setIsExpanded,
    }),
    [hasSidebar, isExpanded, setHasSidebar, setIsExpanded]
  );

  return (
    <SidebarLayoutContext.Provider value={value}>{children}</SidebarLayoutContext.Provider>
  );
}

export function useSidebarLayout() {
  const context = useContext(SidebarLayoutContext);
  if (!context) {
    throw new Error('useSidebarLayout must be used within a SidebarLayoutProvider');
  }
  return context;
}
